import React from 'react';
import CardLayout from '../../../../shared/components/card/CardLayout';
import { Col, Row } from 'react-bootstrap';
import { IPerformanceSummary, IReportingTasksSummary } from '../dto/IReporting';
import { FiInfo } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { numberFormatter } from '../../../../utils/helpers/numberFormater';
import { priceSplitter } from '../../../../utils/helpers/priceSplitter';
import TooltipOverlay from '../../../../shared/components/tooltip/Tooltip';

interface ReportingOverviewProps {
    reportingTasks: IReportingTasksSummary;
    performance: IPerformanceSummary;
}

const ReportingOverview = ({ reportingTasks, performance }: ReportingOverviewProps) => {
    const { t } = useTranslation();
    return (
        <CardLayout>
            <Row>
                <Col className="d-flex justify-content-between align-items-center border-right-lg" lg={4}>
                    <div className="px-1 col-4 col-md-auto">
                        <h5 className="mb-0 text-primary">{reportingTasks?.totalCampaigns ?? 0}</h5>
                        <span>{t('influencer.reporting.campaigns')}</span>
                    </div>
                    <div className="px-1 col-4 col-md-auto">
                        <h5 className="mb-0 text-primary">
                            {reportingTasks?.reportsDue ?? 0}/{reportingTasks?.todoReportsDue ?? 0}
                        </h5>
                        <div className="d-flex align-items-center gap-1">
                            {t('influencer.reporting.reportsDue')}
                            <TooltipOverlay content={t(`influencer.reporting.reportsDueTooltip`)}>
                                <FiInfo size={14} />
                            </TooltipOverlay>
                        </div>
                    </div>
                    <div className="px-1 col-4 col-md-auto">
                        <h5 className="mb-0 text-primary">{reportingTasks?.placements ?? 0}</h5>
                        <div className="d-flex align-items-center gap-1">
                            {t('influencer.reporting.placements')}
                            <TooltipOverlay content={t(`influencer.reporting.placementsTooltip`)}>
                                <FiInfo size={14} />
                            </TooltipOverlay>
                        </div>
                    </div>
                </Col>
                <Col
                    className="d-flex flex-lg-row flex-wrap justify-content-between align-items-center mt-lg-0 mt-2"
                    lg={8}
                >
                    <div className="px-1 col-4 col-md-auto">
                        <h5 className="mb-0 text-primary">{numberFormatter(performance?.touchPoints ?? 0)}</h5>
                        <div className="d-flex align-items-center gap-1">
                            {t('influencer.reporting.touchpoints')}
                            <TooltipOverlay content={t(`influencer.reporting.touchpointsTooltip`)}>
                                <FiInfo size={14} />
                            </TooltipOverlay>
                        </div>
                    </div>
                    <div className="px-1 col-4 col-md-auto">
                        <h5 className="mb-0 text-primary">{priceSplitter(performance?.impressions) ?? 0}</h5>
                        <div className="d-flex align-items-center gap-1">
                            {t('influencer.reporting.impressions')}
                            <TooltipOverlay content={t(`influencer.reporting.impressionsTooltip`)}>
                                <FiInfo size={14} />
                            </TooltipOverlay>
                        </div>
                    </div>
                    <div className="px-1 col-4 col-md-auto">
                        <h5 className="mb-0 text-primary euro-symbol">{priceSplitter(performance?.cpm ?? 0)}</h5>
                        <div className="d-flex align-items-center gap-1">
                            {t('influencer.reporting.cpm')}
                            <TooltipOverlay content={t(`influencer.reporting.cpmTooltip`)}>
                                <FiInfo size={14} />
                            </TooltipOverlay>
                        </div>
                    </div>
                    <div className="px-1 mt-2 mt-md-0 col-4 col-md-auto">
                        <h5 className="mb-0 text-primary euro-symbol">{priceSplitter(performance?.reachCpm)}</h5>
                        <div className="d-flex align-items-center gap-1">
                            {t('influencer.reporting.reachCpm')}
                            <TooltipOverlay content={t(`influencer.reporting.reachCpmTooltip`)}>
                                <FiInfo size={14} />
                            </TooltipOverlay>
                        </div>
                    </div>
                    <div className="px-1 mt-2 mt-md-0 col-4 col-md-auto">
                        <h5 className="mb-0 text-primary">{priceSplitter(performance?.clicks) ?? 0}</h5>
                        <div className="d-flex align-items-center gap-1">
                            {t('influencer.reporting.clicks_total')}
                            <TooltipOverlay content={t(`influencer.reporting.clicksTooltip`)}>
                                <FiInfo size={14} />
                            </TooltipOverlay>
                        </div>
                    </div>
                    <div className="px-1 mt-2 mt-md-0 col-4 col-md-auto">
                        <h5 className="mb-0 text-primary euro-symbol">
                            {priceSplitter(Math.round(performance?.totalEarnings) ?? 0)}
                        </h5>
                        <div className="d-flex align-items-center gap-1">
                            {t('influencer.reporting.totalEarnings')}
                            <TooltipOverlay content={t(`influencer.reporting.totalEarningsTooltip`)}>
                                <FiInfo size={14} />
                            </TooltipOverlay>
                        </div>
                    </div>
                </Col>
            </Row>
        </CardLayout>
    );
};

export default React.memo(ReportingOverview);
